import React from 'react';
import { motion } from 'framer-motion';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  width?: string | number;
  height?: string | number;
  lines?: number;
}

const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  width,
  height,
  lines = 1,
}) => {
  const baseClasses = 'bg-paper-deep';

  const variantClasses = {
    text: 'rounded h-4',
    circular: 'rounded-full',
    rectangular: '',
    rounded: 'rounded-xl',
  };

  const style: React.CSSProperties = {
    width: width || (variant === 'text' ? '100%' : undefined),
    height: height || (variant === 'circular' ? width : undefined),
  };

  if (lines > 1 && variant === 'text') {
    return (
      <div className={`space-y-2 ${className}`}>
        {Array.from({ length: lines }).map((_, i) => (
          <motion.div
            key={i}
            className={`${baseClasses} ${variantClasses[variant]}`}
            style={{ ...style, width: i === lines - 1 ? '70%' : '100%' }}
            animate={{
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.1,
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <motion.div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
      animate={{
        opacity: [0.5, 1, 0.5],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  );
};

// Pre-built skeleton components for common patterns
export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-white border border-zinc-200 rounded-xl p-4 ${className}`}>
    <div className="flex items-center gap-3">
      <Skeleton variant="rounded" width={48} height={48} />
      <div className="flex-1">
        <Skeleton variant="text" width="60%" className="mb-2" />
        <Skeleton variant="text" width="40%" height={12} />
      </div>
    </div>
  </div>
);

export const SkeletonStatCard: React.FC = () => (
  <div className="bg-white border border-zinc-200 rounded-xl p-4">
    <div className="flex items-center gap-3">
      <Skeleton variant="rounded" width={48} height={48} />
      <div>
        <Skeleton variant="text" width={60} height={28} className="mb-1" />
        <Skeleton variant="text" width={80} height={14} />
      </div>
    </div>
  </div>
);

export const SkeletonListItem: React.FC = () => (
  <div className="p-4 flex items-center gap-4">
    <Skeleton variant="circular" width={40} height={40} />
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-2">
        <Skeleton variant="text" width={120} />
        <Skeleton variant="rounded" width={60} height={20} />
      </div>
      <Skeleton variant="text" width="60%" height={14} />
    </div>
    <Skeleton variant="text" width={80} height={14} />
  </div>
);

export const SkeletonReviewCard: React.FC = () => (
  <div className="bg-white border border-zinc-200 rounded-xl p-6">
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <Skeleton variant="rounded" width={40} height={40} />
        <div>
          <Skeleton variant="text" width={120} className="mb-1" />
          <div className="flex items-center gap-2">
            <Skeleton variant="rounded" width={60} height={18} />
            <Skeleton variant="text" width={80} height={12} />
          </div>
        </div>
      </div>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="rounded" width={20} height={20} />
        ))}
      </div>
    </div>
    <Skeleton variant="text" lines={3} />
  </div>
);

export const SkeletonWebsiteTile: React.FC = () => (
  <div className="p-4 rounded-xl bg-white border border-zinc-200">
    <Skeleton variant="text" width="80%" className="mb-1" />
    <Skeleton variant="text" width="60%" height={14} />
  </div>
);

export const SkeletonTable: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
    <div className="divide-y divide-zinc-100">
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonListItem key={i} />
      ))}
    </div>
  </div>
);

export default Skeleton;
